import { Injectable } from '@angular/core';
import {of, Subscription, Observable, BehaviorSubject, take} from 'rxjs';
import { throwError, timer, fromEvent, merge } from 'rxjs';
import {map, catchError, delay, tap, mergeMap, repeat, retryWhen, finalize, timeout} from 'rxjs/operators';
import { User } from '../identity/user.model';
import { NotificationService, NotificationType } from '../shared/notification.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiPaths } from '../../enums/api-paths';
import { LOCALE_ID, Inject } from '@angular/core';
import { GenericErrorResponse, GenericResponse } from '../api-interfaces';
import { Router } from '@angular/router';
import { ConfigService } from '../config.service';
import { LogService } from '../shared/log.service';
import { ErrorService } from '../shared/errors.service';
import {AuthenticationPayload, OtpDeliveryMethod} from './login/login.model';
import {QrCodeResponse} from '../identity/edit-credentials/qr-code-association/qr-code-association.service';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private currentUser: User;

    currentUserChanged = new BehaviorSubject<User>(null);

    constructor(
        private http: HttpClient,
        private notificationService: NotificationService,
        @Inject(LOCALE_ID) public locale: string,
        private router: Router,
        private logger: LogService,
        private errorService: ErrorService
    ) {
        // this.authService.loggedUser
        //   .pipe(take(1))
        //   .subscribe((loggedUser: User) => {
        //     this.loggedUser = loggedUser
        //   });

    }

    private currentUserObserver: Subscription;
    private userActivityObserver: Subscription;

    setCurrentUser(user: User): void {
        const now = new Date();
        localStorage.setItem('last_user_change', now.toString());
        this.currentUser = user;
        this.currentUserChanged.next(this.currentUser);
    }

    updateCurrentIdentity(user: Partial<User>): void {
        this.currentUser = new User({ ...this.currentUser, ...user });
        this.currentUserChanged.next(this.currentUser);
    }

    getCurrentUser(): User {
        return this.currentUser;
    }

    isPasswordExpired(): boolean {
        this.logger.log('checking password expiration');
        const d: Date = new Date(this.getCurrentUser().identities[0].password_expire_date); // TODO: Nel caso di più identità come si gestisce?
        return new Date() > d;
    }

    private display = (response: any) => {
        this.logger.log(response);
    }

    startUserPolling(): void {
        this.logger.log('starting user polling');

        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.PollUserLastChange}`;


        this.currentUserObserver = of({}).pipe(
            mergeMap(_ => this.http.get<any>(url, { withCredentials: true })),
            tap(this.display),
            delay(ConfigService.settings.pollingTimeMs),
            repeat(),
            retryWhen(
                this.genericRetryStrategy({
                    maxRetryAttempts: ConfigService.settings.pollMaxRetries,
                })
            ),
            catchError(error => of(error))

        ).subscribe(
            (response: any) => {
                if (response.esito === 'OK') {
                    this.logger.log('Logged in');
                    const lastUserChange: Date = new Date(response.result);
                    const lastStoredUserChange = new Date(localStorage.getItem('last_user_change'));
                    if (lastUserChange > lastStoredUserChange) {
                      this.logger.log('L\'utente è stato modificato');
                      localStorage.setItem('last_user_change', new Date().toString());
                      this.requestUpdatedUserObservable().subscribe(
                        (response: GenericResponse) => {
                          const user: User = response.result;
                          this.setCurrentUser(user);
                        }
                      );
                    }
                } else {
                    this.logout();
                }
            },
            (err) => {
                this.logger.log(`${err}: polling interrotto`);
                this.logout();
            }

        );
    }

    startUserActivityPolling(): void {
        this.userActivityObserver = merge(
            fromEvent(document.body, 'mousemove'),
            fromEvent(document.body, 'keypress'),
        )
            .pipe(
                timeout(ConfigService.settings.userInactivityTimeout),
                catchError(error => {
                    this.logout();
                    this.router.navigate(['/']);
                    this.notificationService.createNotification(
                        NotificationType.INFO,
                        '',
                        'Sessione sospesa per inattività'
                    );
                    return of(`Sessione scaduta per inattività`);
                })
            )
            .subscribe();
    }

    genericRetryStrategy = ({
        maxRetryAttempts = 3,
        scalingDuration = 1000,
        excludedStatusCodes = []
    }: {
        maxRetryAttempts?: number,
        scalingDuration?: number,
        excludedStatusCodes?: number[]
    } = {}) => (attempts: Observable<any>) => {
        return attempts.pipe(
            mergeMap((error, i) => {
                const retryAttempt = i + 1;
                // if maximum number of retries have been met
                // or response is a status code we don't wish to retry, throw error
                if (
                    retryAttempt > maxRetryAttempts ||
                    excludedStatusCodes.find(e => e === error.status)
                ) {
                    return throwError(error);
                }
                this.logger.log(
                    `Attempt ${retryAttempt}: retrying in ${retryAttempt *
                    scalingDuration}ms`
                );
                // retry after 1s, 2s, etc...
                return timer(retryAttempt * scalingDuration);
            }),
            finalize(() => {
                this.logger.log('max retries exceeded: stop');
                this.logout();
            }
            )
        );
    }

    requestUpdatedUserObservable(): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.RequestUpdatedUser}`;
        return this.http.get<GenericResponse>(url, { withCredentials: true });
    }

    private _handleCmsRequest(url: string, payload: Object, onOkResponse: any): Observable<GenericResponse> {
        return this.http.post<GenericResponse>(url, payload, { withCredentials: true }).pipe(
            map((response: GenericResponse) => {
                if (response.esito === 'OK') {
                    onOkResponse(response);
                } else {
                    this.notificationService.createNotification(
                        NotificationType.ERROR,
                        'Errore',
                        this.errorService.getErrorBycode(response.result.error_code).message
                    );
                }
                return response;
            }),
            catchError((err: any) => {
                const error: GenericErrorResponse = err.error;
                const errorCode = error.result.error_code;
                const errorMessage = this.errorService.getErrorBycode(errorCode).message;
                this.notificationService.createNotification(
                    NotificationType.ERROR,
                    'Errore',
                  errorMessage
                );

                return throwError(
                    'Something bad happened; please try again later.');
            })
        );
    }

    auth(username: string, password: string, otp?: string, otpDeliveryMethod?: OtpDeliveryMethod): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.Auth}`;
        const payload: AuthenticationPayload = {
            username,
            password
        };
        if (otp) {
            payload.otp = otp;
        }
        if (otpDeliveryMethod) {
            payload.otp_delivery_method = otpDeliveryMethod;
        }
        return this.http.post<GenericResponse>(url, payload, { withCredentials: true });
    }

    retrieveAuth(username: string): Observable<void> {
        return this.requestUpdatedUserObservable()
            .pipe(
                map((response: GenericResponse) => {
                    const user: User = response.result;
                    this.logger.log(user);
                    this.setCurrentUser(user);
                    this.logger.log('Recuperati dati aggiornati dell\'utente');
                })
            );
    }

    logout(notification: boolean = true): void {
        this.setCurrentUser(null);
        sessionStorage.removeItem('username');
        this.currentUserObserver?.unsubscribe();
        this.userActivityObserver?.unsubscribe();
        this.router.navigate(['/']);
        if (notification) {
            this.notificationService.createNotification(
                NotificationType.INFO,
                'Logout avvenuto con successo',
                'Arrivederci!');
        }
    }

    resetPassword(temporaryPassword: string, newPassword: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ResetPassword}`;
        const payload = {
            temporary_password: temporaryPassword,
            new_password: newPassword,
        };

        return this._handleCmsRequest(
            url,
            payload,
            (response: GenericResponse) => {
                this.notificationService.createNotification(
                    NotificationType.SUCCESS,
                    'Richiesta inviata con successo',
                    '');
            }
        );
    }

    recoverLostUsername(email: string, cfOPIva: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.RecoverLostUsername}`;
        const payload = {
            email: email,
            fiscal_code: cfOPIva,
        };

        return this._handleCmsRequest(
            url,
            payload,
            (response: GenericResponse) => {
                this.notificationService.createNotification(
                    NotificationType.SUCCESS,
                    'Richiesta inviata con successo',
                    `Ti è stata inviata una email con la username`);
            }
        );
    }

    recoverLostPassword(username: string, code: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.RecoverLostPassword}`;
        const payload = {
            username,
            code,
        };

        return this._handleCmsRequest(
            url,
            payload,
            (response: GenericResponse) => {
                this.logger.log(response);
                const email: string = response.result;
                this.notificationService.createNotification(
                    NotificationType.SUCCESS,
                    'Richiesta inviata con successo',
                    `Ti è stata inviata una email con la password`);
            }
        );
    }

    recoverEmergencyCode(username: string, cfOPiva: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.RecoverEmergencyCode}`;
        const payload = {
            username: username,
            fiscal_code: cfOPiva
        };

        return this._handleCmsRequest(
            url,
            payload,
            (response: GenericResponse) => {
                this.notificationService.createNotification(
                    NotificationType.SUCCESS,
                    'Richiesta inviata con successo',
                    `Riceverai le due parti del codice di emergenza tramite email ed SMS`);
            }
        );
    }

    lockIdentity(userName: string, emergencyCode: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.LockIdentity}`;
        const payload = {
            username: userName,
            emergency_code: emergencyCode,
        };

        return this._handleCmsRequest(
            url,
            payload,
            (response: GenericResponse) => {
                this.notificationService.createNotification(
                    NotificationType.SUCCESS,
                    'Richiesta inviata con successo',
                    'La tua identità digitale è stata bloccata');
            }
        );
    }

    verifyEmail(token: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrlNoApi}/${ApiPaths.ConfirmEmail}`;
        const params = new HttpParams().set('token', token);
        return this.http.get<GenericResponse>(url, {params});
    }

    resendConfirmationEmail(token: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrlNoApi}/${ApiPaths.ResendConfirmationEmail}`;
        const params = new HttpParams().set('token', token);
        return this.http.get<GenericResponse>(url, {params});
    }

    generateAutoLoginQrCode$(): Observable<QrCodeResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.AutologinQrcode}`;
        return this.http.get<QrCodeResponse>(url).pipe(take(1));
    }

    verifyAutoLoginQrCode$(requestId: string): Observable<any> {
        const url = `${environment.OtpGwBaseUrl}/events/${requestId}`;
        return this.http.get<any>(url).pipe(take(1));
    }

    authenticateWithAutoLogin$(jwt: string): Observable<GenericResponse> {
        const url = `${ConfigService.settings.baseUrl}/${ApiPaths.AuthenticateAutologin}`;
        return this.http.post<GenericResponse>(url, {jwt}, {withCredentials: true}).pipe(take(1));
    }
}
